/**
 * Settings → Integrations and Browse apps in words (slice 3b spec §5.1,
 * §5.6; Paper 40–45): the banner after a connect, each row's state, Browse
 * apps' sections, and Tools' "Your apps" line, apart from the components so
 * they're tested alone.
 */
import type {
  Account,
  AccountChange,
  CatalogApp,
  DirectoryEntry,
  Integrations,
  IntegrationApp,
  Level,
  McpAuth,
  McpServer,
  McpTool,
  Provider,
  Started,
} from "./integrations-api";

/** Up to 5 accounts per app (spec §5.1), as the card's footer says; the server decides `can_add`. */
export const MAX_ACCOUNTS_PER_APP = 5;

export type Tone = "moss" | "copper" | "red" | "grey";

export type Banner = { tone: "moss" | "red" | "neutral"; title: string; detail?: string };

/** "Sep 30", in the workspace's timezone. */
export function shortDate(iso: string, timeZone: string): string {
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone });
}

const levelName = (app: IntegrationApp, level: Level) =>
  app.levels.find((l) => l.level === level)?.name ?? level;

/** The account a successful connect made: the reconnected one, else the app's newest. */
function connectedAccount(app: IntegrationApp, started: Started | null): Account | undefined {
  const live = app.accounts.filter((a) => a.status === "active");
  if (started?.account_id) return live.find((a) => a.id === started.account_id);
  return [...live].sort((a, b) => (b.connected_at ?? "").localeCompare(a.connected_at ?? ""))[0];
}

/** `provider` is who the sign-in was with ("Google"), or "the sign-in" when it isn't known. */
type ErrorWords = (app: string, started: Started | null, provider: string) => Banner;

/**
 * Each `?error=` the callback returns (spec §5.2), in words; `app` is the
 * app's name if known. A `Map`, so a code like `__proto__` or `toString`
 * finds nothing and gets the generic words (Codex on #247).
 */
const ERRORS = new Map<string, ErrorWords>(
  Object.entries({
    cancelled: (app, _, provider) => ({
      tone: "neutral",
      title: `${app} didn't connect: ${provider} wasn't given the go-ahead. Try again when you're ready.`,
    }),
    failed: (app, _, provider) => ({
      tone: "red",
      title: `${app} didn't connect: ${provider} didn't finish the sign-in. Try again.`,
    }),
    already_connected: (_, started, provider) => ({
      tone: "red",
      title: "That account is already connected.",
      detail: `To add another ${started ? `${started.name} account` : "account"}, ${
        started?.app === "notion"
          ? "choose another Notion workspace when you sign in."
          : `sign in to ${provider} as someone else.`
      }`,
    }),
    different_account: (_, started) => ({
      tone: "red",
      title: "That's a different account. Use Add account to connect it.",
      detail: started?.label
        ? `${started.label} is unchanged.`
        : "The account you reconnected is unchanged.",
    }),
    wrong_session: () => ({
      tone: "red",
      title: "That sign-in finished in another browser or account, so nothing was connected.",
      detail: "Start it again here.",
    }),
    identity_unavailable: (app, _, provider) => ({
      tone: "red",
      title: `${app} didn't connect: Crew couldn't tell which ${provider} account it was.`,
      detail: "Nothing was connected. Try again in a minute.",
    }),
    wrong_user: () => ({
      tone: "red",
      title: "That sign-in was for someone else's Crew, so nothing was connected.",
    }),
    link_invalid: () => ({
      tone: "neutral",
      title: "That sign-in link had expired, so nothing changed. Start again when you're ready.",
    }),
    unknown: () => ({
      tone: "neutral",
      title: "That sign-in had already finished or been cancelled. Nothing changed.",
    }),
    unavailable: () => ({
      tone: "neutral",
      title: "Couldn't reach the sign-in service, so nothing changed. Try again in a few minutes.",
    }),
    mcp_unchanged: () => ({
      tone: "red",
      title: "Signed in again, but Crew couldn't read the server's tools that way.",
      detail: "It keeps its old sign-in. Try again in a minute, or remove it and add it again.",
    }),
    // A failed first read leaves nothing (Codex on #249).
    mcp_unreachable: () => ({
      tone: "red",
      title: "Signed in, but Crew couldn't read the server's tools, so it wasn't added.",
      detail: "Check its address, or try again in a minute.",
    }),
  } satisfies Record<string, ErrorWords>),
);

/** Why an MCP server's first read failed, by the callback's `reason` code. */
const READ_FAILURES = new Map<string, string>(
  Object.entries({
    too_many_tools: "It has more than 500 tools, more than Crew can take.",
    refused: "The server refused. Check its address, or try again.",
    unavailable: "It didn't answer. Try again in a minute.",
  }),
);

const OTHER_ERROR: Banner = { tone: "neutral", title: "That didn't connect. Nothing changed." };

/** The app's name: from the list, else from what this tab remembered, else its slug. */
function nameOf(slug: string, app: IntegrationApp | undefined, started: Started | null): string {
  if (app) return app.name;
  return started?.app === slug ? started.name : slug;
}

/** `?connected=<app>`: the app, and the account it made at its level once the list has it. */
function connectedBanner(
  slug: string,
  apps: IntegrationApp[] | undefined,
  started: Started | null,
): Banner {
  const app = apps?.find((a) => a.app === slug);
  const verb = started?.account_id ? "reconnected" : "connected";
  const account = app && connectedAccount(app, started);
  if (!account) return { tone: "moss", title: `${nameOf(slug, app, started)} is ${verb}.` };
  // An account whose email isn't known yet has no label: the sentence leaves it out.
  const who = account.label ?? account.email;
  const level = levelName(app, account.level);
  return {
    tone: "moss",
    title: who ? `${app.name} is ${verb}: ${who}, ${level}.` : `${app.name} is ${verb}, ${level}.`,
  };
}

/**
 * The banner a connect comes back to (spec §5.1, §5.2): `?connected=<app>`,
 * or each `?error=` the callback returns. `started` is what this tab
 * remembered when it left, so the words can name the app and the account.
 */
export function returnBanner(
  query: { connected?: string | null; error?: string | null; reason?: string | null },
  ctx: { apps: IntegrationApp[] | undefined; started: Started | null },
): Banner | null {
  // An MCP server's sign-in (spec §6.2): `mcp:<name>`, and its tools come next.
  if (query.connected?.startsWith("mcp:")) {
    const name = query.connected.slice(4);
    return { tone: "moss", title: `${name} is connected. Choose which of its tools are on.` };
  }
  if (query.connected) return connectedBanner(query.connected, ctx.apps, ctx.started);
  if (!query.error) return null;
  const words = ERRORS.get(query.error);
  const provider = ctx.started?.provider ?? "the sign-in";
  const banner = words ? words(ctx.started?.name ?? "The app", ctx.started, provider) : OTHER_ERROR;
  // A failed MCP add's reason, a code from the callback (Devin on #249).
  const reason = query.error === "mcp_unreachable" ? READ_FAILURES.get(query.reason ?? "") : null;
  return reason ? { ...banner, detail: reason } : banner;
}

export type RowState = {
  text: string;
  tone: Tone;
  /** The line under the name: the email, "Connected Sep 30", or what it's waiting for. */
  under?: string;
  /** A Needs reconnecting row's reason, or a failed revoke's. */
  reason?: string;
  /** A Reconnect waiting on its sign-in: "Waiting for sign-in · 7:42". */
  wait?: string;
};

const revokeFailed = (provider: string) =>
  `${provider} didn't confirm the sign-out. Your crew can't use this account. Try again, or remove Crew's access in your ${provider} account.`;

/** Under a renamed account its email; under an unnamed one, when it connected. */
function underName(a: Account, timeZone: string): string | undefined {
  if (a.name) return a.email ?? undefined;
  return a.connected_at ? `Connected ${shortDate(a.connected_at, timeZone)}` : undefined;
}

function expiredReason(a: Account, timeZone: string, provider: string): string {
  if (a.reconnecting) return `Waiting for you to sign in with ${provider} again.`;
  const on = a.expired_at ? ` on ${shortDate(a.expired_at, timeZone)}` : "";
  return `${provider} ended the sign-in${on}. Your crew can't use this account until you reconnect.`;
}

/**
 * An account's state on its row (spec §5.1; Paper 44's table). `now` is the
 * server's time; `provider` who the account signs in with ("Google").
 */
export function rowState(
  a: Account,
  timeZone: string,
  local: { disconnecting?: boolean; now?: number; provider: string },
): RowState {
  const { provider } = local;
  const under = underName(a, timeZone);
  // From the moment the owner confirms, whatever the list says until it refetches (PR 6).
  if (local.disconnecting) return { text: "Disconnecting…", tone: "grey", under };
  const wait = waitLine(a.expires_at, local.now);
  switch (a.status) {
    case "connecting":
      return {
        text: "Connecting…",
        tone: "grey",
        under: wait ?? `Waiting for you to sign in with ${provider}`,
      };
    case "disconnecting":
      return local.disconnecting
        ? { text: "Disconnecting…", tone: "grey", under }
        : {
            text: "Couldn't finish disconnecting",
            tone: "red",
            under,
            reason: revokeFailed(provider),
          };
    case "expired":
      return {
        text: "Needs reconnecting",
        tone: "red",
        under: a.name ? under : undefined,
        reason: expiredReason(a, timeZone, provider),
        ...(a.reconnecting && wait ? { wait } : {}),
      };
    default:
      return {
        text: "Connected",
        tone: "moss",
        under,
        ...(a.reconnecting && wait ? { wait } : {}),
      };
  }
}

// ── The wait for a sign-in (spec §5.1) ────────────────────────────────────

const at = (iso: string | null | undefined) => (iso ? Date.parse(iso) : NaN);

/**
 * The server's clock less this browser's, from its `now` and when the
 * answer arrived. The server stamps `now` just before it answers (after its
 * tidy), so this errs only by the answer's download: a countdown can run a
 * little late, never early (Devin on #254). 0 with no `now`.
 */
export function clockSkew(now: string | undefined, received: number): number {
  const server = at(now);
  return Number.isFinite(server) ? Math.round(server - received) : 0;
}

/** "7:42": the minutes and seconds until `expiresAt`, at the server's `now`, never below zero. */
export function waitLeft(expiresAt: string, now: number): string {
  const s = Math.max(0, Math.ceil((at(expiresAt) - now) / 1_000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/** "Waiting for sign-in · 7:42", or null with no time to count down. */
function waitLine(expiresAt: string | null | undefined, now: number | undefined): string | null {
  if (now === undefined || !Number.isFinite(at(expiresAt))) return null;
  return `Waiting for sign-in · ${waitLeft(expiresAt!, now)}`;
}

/** When each sign-in on the page stops waiting (ms), for the clock. */
export function waitEnds(list: Integrations): number[] {
  return [
    ...list.apps.flatMap((app) => app.accounts.map((a) => at(a.expires_at))),
    ...list.mcp_servers.map((s) => at(s.expires_at)),
  ].filter(Number.isFinite);
}

const passed = (iso: string | null | undefined, now: number) => at(iso) <= now;

/**
 * The list as it stands at the server's `now`: a connect whose link has
 * expired is gone (and its app's card, with no account left), a Reconnect's
 * is back to the account's normal state, and an OAuth server still being
 * added is gone, as the server's tidy leaves them. The same list when
 * nothing has passed.
 */
export function liveWaits(list: Integrations, now: number): Integrations {
  const ended = (iso: string | null | undefined) => passed(iso, now);
  const any =
    list.apps.some((app) => app.accounts.some((a) => ended(a.expires_at))) ||
    list.mcp_servers.some((s) => ended(s.expires_at));
  if (!any) return list;
  const apps = list.apps
    .map((app) => ({
      ...app,
      accounts: app.accounts
        .filter((a) => !(a.status === "connecting" && ended(a.expires_at)))
        .map((a) => (ended(a.expires_at) ? { ...a, reconnecting: false, expires_at: null } : a)),
    }))
    .filter((app) => app.accounts.length > 0);
  return { ...list, apps, mcp_servers: list.mcp_servers.filter((s) => !ended(s.expires_at)) };
}

/** What this page is doing to a server right now (Remove under way), and the server's time. */
export type ServerLocal = { removing?: boolean; now?: number };

/** A server's state on its card (spec §6.2; Paper 44). */
export function serverState(s: McpServer, local: ServerLocal = {}): { text: string; tone: Tone } {
  // From the moment the owner confirms Remove, until the list refetches (PR 6).
  if (local.removing) return { text: "Removing…", tone: "grey" };
  if (s.removing) return { text: "Couldn't finish removing", tone: "red" };
  if (s.status === "active") return { text: "Connected", tone: "moss" };
  if (s.status === "error") return { text: "Couldn't reach it", tone: "copper" };
  if (s.status === "expired") return { text: "Needs reconnecting", tone: "red" };
  return { text: "Connecting…", tone: "grey" };
}

/** Under a server's tool counts, and its reason (Paper 44's "Under the name"). */
export function serverLines(
  s: McpServer,
  timeZone: string,
  local: ServerLocal = {},
): { tools: string; under: string; reason?: string } {
  const tools = `${plural(s.tools.on, "tool")} on · ${s.tools.off} off`;
  if (local.removing) return { tools, under: "Being removed" };
  if (s.removing) return { tools, under: "Being removed", reason: s.error };
  switch (s.status) {
    case "connecting":
      return {
        tools,
        under:
          s.auth === "oauth"
            ? (waitLine(s.expires_at, local.now) ?? "Waiting for you to sign in")
            : "Reading its tools…",
      };
    case "expired":
      return {
        tools,
        under: s.synced_at ? `Refreshed ${shortDate(s.synced_at, timeZone)}` : "",
        reason: `The sign-in to ${s.name} ended. Your crew can't use its tools until you reconnect.`,
      };
    case "error":
      return { tools, under: "Last refresh failed", reason: s.error };
    default:
      return { tools, under: s.synced_at ? `Refreshed ${shortDate(s.synced_at, timeZone)}` : "" };
  }
}

/** The guide line on a server's card (slice 6k §5): only while it's otherwise fine. */
export function serverGuideLine(
  s: McpServer,
  local: ServerLocal = {},
): "changed" | "unreadable" | null {
  if (s.status !== "active" || s.removing || local.removing) return null;
  if (s.guides?.changed) return "changed";
  return s.guides?.unreadable ? "unreadable" : null;
}

/** A guide file's text to read: what follows its frontmatter, which is for the agent. */
export const guideBody = (text: string) => text.replace(/^---\r?\n[\s\S]*?\r?\n---\r?\n+/, "");

/** A guide file's tab: owners never need `SKILL.md`, and a guide names the others by path. */
export const guideTab = (path: string) => (path === "SKILL.md" ? "Guide" : path);

export type ServerAction = "tools" | "refresh" | "reconnect" | "remove";

/**
 * Whether a card may start `a` now: Tools (a window, which changes nothing)
 * always; a Refresh, Reconnect, Try again or Remove only when no other change
 * to the server is running (Codex on #251).
 */
export const serverActionAllowed = (a: ServerAction, busy: boolean) => a === "tools" || !busy;

/**
 * The button under a server's name: Reconnect when its sign-in ended; Try
 * again when a new server's sign-in link may have expired or been closed
 * (PR 6); none while Remove runs.
 */
export function serverButton(s: McpServer, local: ServerLocal = {}): "reconnect" | "retry" | null {
  if (local.removing) return null;
  if (s.status === "expired") return "reconnect";
  return s.status === "connecting" && s.auth === "oauth" ? "retry" : null;
}

/**
 * A server's ⋯ menu, by its state (Paper 44): a server with no sign-in has
 * no Reconnect, and one still being added only Remove, or for OAuth, Try
 * again (a fresh sign-in link: the last one may have expired or been closed).
 */
export function serverMenu(s: McpServer): ServerAction[] {
  const reconnect: ServerAction[] = s.auth === "none" ? [] : ["reconnect"];
  if (s.removing) return ["remove"];
  switch (s.status) {
    case "connecting":
      return s.auth === "oauth" ? ["reconnect", "remove"] : ["remove"];
    case "expired":
      return [...reconnect, "tools", "remove"];
    case "error":
      return ["refresh", "tools", "remove"];
    default:
      return ["tools", "refresh", ...reconnect, "remove"];
  }
}

/** A tool's own claim about itself, as its list tags it (Paper 43). */
export function toolTag(t: McpTool): { text: string; tone: Tone } {
  if (t.read_only) return { text: "Read-only", tone: "moss" };
  if (t.destructive) return { text: "Deletes", tone: "red" };
  return { text: "Changes things", tone: "copper" };
}

/** The tool list's footnote (Paper 43): when they were last read, and whose labels they are. */
export function toolsNote(syncedAt: string | null, timeZone: string): string {
  const when = syncedAt ? `Refreshed ${shortDate(syncedAt, timeZone)}. ` : "";
  return `${when}The labels are the server's own.`;
}

/** "9 on · 4 off". */
export const toolCounts = (tools: McpTool[]) => {
  const on = tools.filter((t) => t.enabled).length;
  return `${on} on · ${tools.length - on} off`;
};

// ── Add MCP server (spec §6.2, §6.4) ─────────────────────────────────────

export type ServerForm = {
  name: string;
  url: string;
  auth: McpAuth;
  apiKey: string;
  header: string;
  format: string;
  directoryId: string | null;
};

export const EMPTY_SERVER_FORM: ServerForm = {
  name: "",
  url: "",
  auth: "none",
  apiKey: "",
  header: "Authorization",
  format: "Bearer {key}",
  directoryId: null,
};

/** A directory name as a server name: lowercased to `a–z0–9-`, at most 30 (spec §6.4). */
export function serverName(name: string): string {
  const slug = name
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 30)
    .replace(/-+$/, "");
  return slug || "server";
}

/** Add MCP server filled in from a directory entry (spec §6.4): `unknown` preselects None. */
export function directoryForm(e: DirectoryEntry): ServerForm {
  return {
    name: serverName(e.name),
    url: e.url,
    auth: e.auth === "unknown" ? "none" : e.auth,
    apiKey: "",
    header: e.header ?? "Authorization",
    format: e.format ?? "Bearer {key}",
    directoryId: e.id,
  };
}

/** The note above the fields for a directory entry (spec §6.4). */
export function directoryNote(e: DirectoryEntry): {
  title: string;
  checked: boolean;
  line: string;
} {
  return {
    title: `${e.name}, from the directory`,
    checked: e.checked,
    line: e.checked
      ? "Checked with Crew"
      : "Not checked with Crew yet. If its sign-in doesn't work here, you'll see why and nothing is added.",
  };
}

/** Why the form can't be sent yet, by field; empty when it can. */
export function serverFormProblems(f: ServerForm): Partial<Record<keyof ServerForm, string>> {
  const out: Partial<Record<keyof ServerForm, string>> = {};
  if (!/^[a-z0-9-]{1,30}$/.test(f.name.trim())) {
    out.name = "Lowercase letters, numbers and dashes, up to 30.";
  }
  let url: URL | null = null;
  try {
    url = new URL(f.url.trim());
  } catch {
    url = null;
  }
  if (!url || url.protocol !== "https:" || url.username || url.password) {
    out.url = "A public https address, with no password in it.";
  }
  if (f.auth === "api_key") {
    if (!f.apiKey.trim()) out.apiKey = "The server's key.";
    if (!f.format.includes("{key}")) out.format = "Put {key} where the key goes.";
    if (!f.header.trim()) out.header = "A header name, such as Authorization.";
  }
  return out;
}

/** `POST /api/integrations/mcp`'s body: the key only for an API-key server. */
export function serverBody(f: ServerForm) {
  return {
    name: f.name.trim(),
    url: f.url.trim(),
    auth: f.auth,
    ...(f.auth === "api_key"
      ? { api_key: f.apiKey.trim(), header: f.header.trim(), format: f.format.trim() }
      : {}),
    ...(f.directoryId ? { directory_id: f.directoryId } : {}),
  };
}

/** How a directory entry signs in, in a word (spec §5.6). */
export const directoryAuth = (e: DirectoryEntry) =>
  e.auth === "unknown" ? "Not listed" : AUTH_WORDS[e.auth];

/** Directory entries shown before "Show all" (spec §5.6). */
export const DIRECTORY_FIRST = 8;

/** The directory as Browse apps lists it: Checked first, searched over names, blurbs and domains. */
export function directoryView(entries: DirectoryEntry[], query: string): DirectoryEntry[] {
  const q = query.trim().toLowerCase();
  return entries
    .filter((e) => !q || `${e.name} ${e.blurb} ${e.domain}`.toLowerCase().includes(q))
    .sort((a, b) => Number(b.checked) - Number(a.checked) || a.name.localeCompare(b.name));
}

export const AUTH_WORDS: Record<McpServer["auth"], string> = {
  none: "No sign-in",
  api_key: "API key",
  oauth: "OAuth",
};

/** A URL without its scheme, as the phone's server card shows it. */
export const bareUrl = (url: string) => url.replace(/^https?:\/\//, "");

/** Mock mode's stand-in for Google's consent screen, on the API (spec §8). */
const MOCK_CONSENT = "/api/integrations/mock-connect";

/**
 * The hosts a connect link may open on. Composio's link call returns
 * `https://connect.composio.dev/link/lk_…`, which redirects (307) to
 * `dashboard.composio.dev`, never straight to Google: recorded in S-241's
 * `test/canary/s-241/out-dcr-link.json` for an MCP server, and checked live
 * for a Gmail (Composio-managed) link on `crew-dev`, 2026-10-04 (Codex on #247).
 */
const COMPOSIO_LINK_HOSTS = new Set(["connect.composio.dev", "dashboard.composio.dev"]);

/**
 * Where a connect may send the browser (Codex on #247): Composio's link over
 * https on one of those hosts, or mock mode's consent page on the API's own
 * origin. Anything else is null, and the connect is cancelled.
 */
export function connectTarget(url: string, apiOrigin: string): string | null {
  let target: URL;
  try {
    target = new URL(url, apiOrigin);
  } catch {
    return null;
  }
  if (target.protocol === "https:" && COMPOSIO_LINK_HOSTS.has(target.hostname)) return target.href;
  const own = new URL(apiOrigin).origin;
  if (target.origin === own && target.pathname === MOCK_CONSENT) return target.href;
  return null;
}

/**
 * What Disconnect's confirm says (spec §5.4): a sign-out at the provider only
 * where Crew can count on one (Google); elsewhere, that Crew deletes its
 * connection, and where the owner removes Crew too (Codex on #257).
 */
export function disconnectWords(provider: Provider): {
  line: string;
  link?: { href: string; text: string };
} {
  if (provider.revokes) {
    return {
      line: `Your crew loses access to this account. Crew also signs it out at ${provider.name}, so nothing keeps working in the background.`,
    };
  }
  return {
    line: `Your crew loses access to this account, and Crew deletes its connection. To be sure nothing keeps working, also remove Crew in your ${provider.name} account.`,
    link: { href: provider.access, text: `Your ${provider.name} connected apps` },
  };
}

/** The notice after a Disconnect (spec §5.3, §5.4). */
export function disconnectNotice(input: {
  label: string;
  app: string;
  /** The account that's primary now, when the disconnected one was. */
  next: string | null;
  /**
   * False when Composio wouldn't revoke it (an expired account) or can't
   * revoke the app: the provider may still honour it.
   */
  revoked: boolean;
  /** Who it signed in with: "Google". */
  provider: string;
}): string {
  const parts = [`${input.label} is disconnected.`];
  if (input.next) parts.push(`${input.next} is now the primary ${input.app} account.`);
  if (!input.revoked) {
    parts.push(
      // Google revokes every account Composio can: only an expired one isn't, for its last hour.
      input.provider === "Google"
        ? "Google may honour its last access for up to an hour."
        : `${input.provider} may still honour its access: remove Crew in your ${input.provider} account to be sure.`,
    );
  }
  return parts.join(" ");
}

/** Change access's request: the new level, or nothing when it's the same (spec §5.3). */
export function accessChange(a: Account, level: Level): AccountChange | null {
  return a.level === level ? null : { level };
}

/** "2 accounts". */
export const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? "" : "s"}`;

// ── Browse apps ────────────────────────────────────────────────────────────

export type Chip = { key: string; label: string };

/** The chip that shows only the MCP directory (Browse servers, `?filter=mcp`). */
export const MCP_CHIP = "mcp";

/**
 * All, then each category with an app in it, in the catalog's order, then
 * MCP servers when the directory has any (spec §5.6).
 */
export function catalogChips(apps: CatalogApp[], directory: DirectoryEntry[] = []): Chip[] {
  const seen = new Map<string, string>();
  for (const a of apps) if (!seen.has(a.category)) seen.set(a.category, a.category_name);
  return [
    { key: "all", label: `All · ${apps.length}` },
    ...[...seen].map(([key, label]) => ({ key, label })),
    ...(directory.length > 0 ? [{ key: MCP_CHIP, label: "MCP servers" }] : []),
  ];
}

export type Section = { category: string; name: string; apps: CatalogApp[] };

/** The apps by category, searched over names and blurbs; a category with none isn't shown. */
export function catalogSections(
  apps: CatalogApp[],
  view: { query: string; category: string },
): Section[] {
  const q = view.query.trim().toLowerCase();
  const sections: Section[] = [];
  if (view.category === MCP_CHIP) return [];
  for (const a of apps) {
    if (view.category !== "all" && a.category !== view.category) continue;
    if (q && !`${a.name} ${a.blurb}`.toLowerCase().includes(q)) continue;
    let section = sections.find((s) => s.category === a.category);
    if (!section) {
      section = { category: a.category, name: a.category_name, apps: [] };
      sections.push(section);
    }
    section.apps.push(a);
  }
  return sections;
}

// ── Yours and Shared with you (slice 6a §6.4) ──────────────────────────────

/** Another person's shared account or server, as Shared with you lists it. */
export type SharedRow =
  | { kind: "account"; id: string; app: IntegrationApp; account: Account; user: Account["user"] }
  | { kind: "server"; id: string; server: McpServer; user: McpServer["user"] };

/**
 * The list split by whose it is (slice 6a §6.4): the person's own accounts
 * and servers, as the page has always listed them (a card per app with one
 * of theirs, its `can_add` theirs), and the others' shared ones.
 */
export function byConnector(
  list: Integrations,
  me: string,
): { mine: Integrations; shared: SharedRow[] } {
  const apps = list.apps
    .map((app) => ({ ...app, accounts: app.accounts.filter((a) => a.user.id === me) }))
    .filter((app) => app.accounts.length > 0);
  const shared: SharedRow[] = [
    ...list.apps.flatMap((app) =>
      app.accounts
        .filter((a) => a.user.id !== me)
        .map((a) => ({ kind: "account" as const, id: a.id, app, account: a, user: a.user })),
    ),
    ...list.mcp_servers
      .filter((s) => s.user.id !== me)
      .map((s) => ({ kind: "server" as const, id: s.id, server: s, user: s.user })),
  ];
  return {
    mine: { ...list, apps, mcp_servers: list.mcp_servers.filter((s) => s.user.id === me) },
    shared,
  };
}

/** A person's first name, as the page names them. */
export const firstName = (name: string) => name.trim().split(/\s+/)[0] || name;

/**
 * A shared row in words (Paper 74, 74b): its title ("Gmail · Work"), what's
 * under it on a desktop (the email, or the server's address) and on a phone
 * (with the level, or the tools that are on), its level column, its state,
 * and when it needs reconnecting, who to ask.
 */
export function sharedRowView(row: SharedRow, timeZone: string, now?: number) {
  const who = firstName(row.user.name);
  if (row.kind === "server") {
    const s = row.server;
    const state = serverState(s);
    const tools = `${plural(s.tools.on, "tool")} on`;
    return {
      title: s.name,
      under: bareUrl(s.url),
      underPhone: `MCP server · ${tools}`,
      level: tools,
      state,
      ask: s.status === "expired" ? `Ask ${who} to reconnect it.` : null,
    };
  }
  const { app, account: a } = row;
  const level = levelName(app, a.level);
  const email = a.email ?? a.label ?? app.name;
  return {
    title: a.name ? `${app.name} · ${a.name}` : app.name,
    under: email,
    underPhone: `${email} · ${level}`,
    level,
    state: rowState(a, timeZone, { now, provider: app.provider.name }),
    ask: a.status === "expired" ? `Ask ${who} to reconnect it.` : null,
  };
}

/** Under Shared with you on a phone (Paper 74b): who shared them, and that your own comes first. */
export function sharedNote(rows: SharedRow[]): string {
  const names = [...new Set(rows.map((r) => firstName(r.user.name)))];
  const by =
    names.length <= 2
      ? names.join(" and ")
      : `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`;
  return `Shared by ${by}. When you ask, your own account comes first.`;
}

/** Who can use it, Just you: only the person's own asks. */
export const JUST_YOU_WORDS = "Only turns you ask for can use it.";

/**
 * Who can use it, Shared, and its confirm (spec §10, choice 5): what sharing
 * means, at the reach it has ("up to Read only").
 */
export function shareWords(reach: string): string {
  return `Every turn in the crew can use it, ${reach}. Others see it under Shared with you.`;
}

// ── Settings → Tools ───────────────────────────────────────────────────────

/** Tools' "Your apps" row (spec §5.1): what's connected, or what can be. */
export function yourAppsLine(list: Integrations): string {
  const accounts = list.apps.flatMap((a) => a.accounts).filter((a) => a.status !== "connecting");
  const servers = list.mcp_servers.length;
  if (accounts.length === 0 && servers === 0) return "Gmail, Calendar and your own MCP servers";
  const parts = [];
  if (accounts.length > 0) parts.push(plural(accounts.length, "account"));
  if (servers > 0) parts.push(plural(servers, "MCP server"));
  return parts.join(" and ");
}
